import { PlusCircle, Export, Upload } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { PageInfoBanner } from '@/shared/components/walkthrough';
import { useSession } from '@clerk/clerk-react';
import { DueDiligenceCategoryList } from 'components/dueDiligence/DueDiligenceCategoryList';
import { useDueDiligenceCategories, useUpdateDueDiligenceCategory } from 'hooks/useDueDiligence';
import { DueDiligenceCategory } from 'shared/types/due-diligence';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const DueDiligenceCategoriesTab = () => {
  const navigate = useNavigate();
  const { session } = useSession();
  const { categories, isLoading, refetch } = useDueDiligenceCategories();
  const { updateCategory } = useUpdateDueDiligenceCategory();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [pendingImportData, setPendingImportData] = useState<any>(null);
  const [importMode, setImportMode] = useState<'merge' | 'replace'>('merge');

  const handleEdit = (category: DueDiligenceCategory) => {
    navigate(`/settings/due-diligence-categorieen/${category.id}`);
  };

  const handleDeactivate = async (category: DueDiligenceCategory) => {
    try {
      await updateCategory(category.id, {
        active: !category.active,
      });
      toast.success(
        category.active
          ? `Categorie "${category.name}" gedeactiveerd`
          : `Categorie "${category.name}" geactiveerd`
      );
      refetch();
    } catch (error) {
      toast.error('Fout bij (de)activeren van categorie');
    }
  };

  // Export all DD questions to JSON file
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/export`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error('Export mislukt');
      }

      // Get the JSON data
      const data = await response.json();

      // Create a download link
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dd-questions-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('DD vragen geëxporteerd');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Fout bij exporteren van DD vragen');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle file selection for import
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        if (!data.categories || !Array.isArray(data.categories)) {
          toast.error('Ongeldig bestandsformaat: categories array verwacht');
          return;
        }
        setPendingImportData(data);
        setShowImportConfirm(true);
      } catch (error) {
        toast.error('Ongeldig JSON bestand');
      }
    };
    reader.readAsText(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Execute the import
  const executeImport = async () => {
    if (!pendingImportData) return;

    setIsImporting(true);
    setShowImportConfirm(false);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/due-diligence-categories/import?mode=${importMode}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(pendingImportData),
        },
        session
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Import mislukt');
      }

      // Show detailed results
      const { results } = result;
      let message = `Import voltooid: ${results.categoriesCreated} categorieën aangemaakt, ${results.categoriesUpdated} bijgewerkt`;
      message += `\n${results.questionsCreated} vragen aangemaakt, ${results.questionsUpdated} bijgewerkt`;
      message += `\n${results.optionsCreated} opties aangemaakt, ${results.optionsUpdated} bijgewerkt`;

      if (results.labelsNotFound?.length > 0) {
        message += `\n\n⚠️ Labels niet gevonden: ${results.labelsNotFound.join(', ')}`;
      }

      toast.success(message, { autoClose: 10000 });
      refetch();
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(`Fout bij importeren: ${error.message}`);
    } finally {
      setIsImporting(false);
      setPendingImportData(null);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageInfoBanner pageId="dd-settings" />

      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Due Diligence Categorieën
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Beheer categorieën en vragen voor due diligence controles op gebouw- en unit niveau
          </p>
        </div>
        <div className="flex items-center gap-2" data-tour="dd-import-export">
          {/* Export Button */}
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isExporting}
          >
            <Export className="h-4 w-4 mr-2" />
            {isExporting ? 'Exporteren...' : 'Exporteren'}
          </Button>

          {/* Import Button */}
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isImporting}
          >
            <Upload className="h-4 w-4 mr-2" />
            {isImporting ? 'Importeren...' : 'Importeren'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleFileSelect}
          />

          {/* New Category Button */}
          <Button data-tour="dd-add-category" onClick={() => navigate('/settings/due-diligence-categorieen/nieuw')}>
            <PlusCircle className="h-4 w-4 mr-2" />
            Nieuwe Categorie
          </Button>
        </div>
      </div>

      {/* Categories List */}
      <div data-tour="dd-category-list">
        <DueDiligenceCategoryList
          categories={categories}
          onEdit={handleEdit}
          onDeactivate={handleDeactivate}
          isLoading={isLoading}
        />
      </div>

      {/* Import Confirmation Dialog */}
      <AlertDialog open={showImportConfirm} onOpenChange={setShowImportConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>DD Vragen Importeren</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                Je staat op het punt om{' '}
                <strong>{pendingImportData?.categories?.length || 0} categorieën</strong> te importeren.
              </p>
              <div className="space-y-2">
                <p className="font-medium">Kies een import modus:</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="importMode"
                      value="merge"
                      checked={importMode === 'merge'}
                      onChange={() => setImportMode('merge')}
                    />
                    <span>
                      <strong>Samenvoegen</strong> - Bestaande categorieën/vragen bijwerken, nieuwe toevoegen
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="importMode"
                      value="replace"
                      checked={importMode === 'replace'}
                      onChange={() => setImportMode('replace')}
                    />
                    <span>
                      <strong>Vervangen</strong> - Alle bestaande deactiveren en opnieuw importeren
                    </span>
                  </label>
                </div>
              </div>
              <p className="text-sm text-amber-600">
                ⚠️ Document labels worden gekoppeld op basis van naam. Niet-gevonden labels worden overgeslagen.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingImportData(null)}>
              Annuleren
            </AlertDialogCancel>
            <AlertDialogAction onClick={executeImport}>
              Importeren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DueDiligenceCategoriesTab;
